/**
 * Level 100's boss, Super Drunk (02 §9.4), on I-ticks: D_1D84, which the IRQ's sprite handler runs
 * after its sprite loop (D_1CBD, after L_1D24) while the boss is on (D_5AFF), in place of the monsters'
 * contact test. The boss keeps its state in zero page: X $BC, Y $BD, picture $BE (9 while stunned),
 * way $BF, colour $C0. It appears as the READY banner goes (D_1694 on level 100: X $74, Y $65, going
 * up and left) and bounces in X $28..$C0, Y $3A..$B8, 2 px a move - two moves an I-tick once angry.
 * After each move (L_1DEA) each player in normal play (player 2 first) within -8..63 px across and
 * -8..55 px down of it dies - or, if it is stunned, wins the game (L_1E13): the boss is gone at once
 * ($1E2E clears its sprites) and the ending counts 1,000,000 into the winner's score over 100 frames.
 */

import { addScore } from '../../score';
import { killPlayer } from '../../players';
import { initialBossAi } from '../state';
import { BOSS } from '../tuning';
import type { Boss, World } from '../../types';

/** The boss of level 100, waiting for the READY banner to go; null on every other level. */
export function makeBoss(world: World): Boss | null {
  if (world.level.n !== 100) return null;
  const ai = initialBossAi();
  ai.wake = BOSS.wake;
  return { x: BOSS.x, y: BOSS.y, hp: BOSS.hits, state: 'fight', angry: false, blink: true, ai };
}

/** Within the box: -8 <= d < reach in 8-bit arithmetic, as the C64 compares (L_1DEC; $1134). */
export const inReach = (d: number, reach: number): boolean => {
  const b = d & 0xff;
  return b < reach || b >= 0x100 - BOSS.near;
};

/** D_1D96's two bounces: a side flips the way and the picture set; the top or bottom flips the way. */
function move(b: Boss): void {
  const a = b.ai;
  if (a.dx < 0 ? b.x < BOSS.minX : b.x >= BOSS.maxX) {
    a.dx = -a.dx;
    a.face ^= BOSS.flip;
  } else {
    b.x = (b.x + a.dx) & 0xff;
  }
  if (a.dy < 0 ? b.y < BOSS.minY : b.y >= BOSS.maxY) a.dy = -a.dy;
  else b.y = (b.y + a.dy) & 0xff;
}

/** L_1DEC: the players' touch - death, or the win (true). */
function touch(world: World, b: Boss): boolean {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play' || p.invulnerable > 0) continue;
    if (!inReach(p.x - b.x, BOSS.reachX) || !inReach(p.y - b.y, BOSS.reachY)) continue;
    if (b.state !== 'stunned') {
      killPlayer(world, id);
      continue;
    }
    b.state = 'beaten';
    b.blink = true;
    b.ai.winner = id;
    b.ai.payout = BOSS.payoutTicks;
    world.events.push({ type: 'won', player: id });
    return true;
  }
  return false;
}

/** D_1D96: one move (none while stunned), then the touch. True if it was won. */
function update(world: World, b: Boss): boolean {
  if (b.state === 'fight') move(b);
  return touch(world, b);
}

/**
 * One I-tick of the boss: the wait for the READY banner (play only), the count-up after the win, and
 * D_1D84 - twice when angry. Nothing moves while play is frozen.
 */
export function bossITick(world: World): void {
  const b = world.boss;
  if (!b) return;
  const a = b.ai;
  if (b.state === 'beaten') {
    if (a.payout > 0 && (a.winner === 0 || a.winner === 1)) {
      a.payout--;
      addScore(world, a.winner, BOSS.payoutUnits);
    }
    return;
  }
  if (world.flow.freeze > 0) return;
  if (a.wake > 0) {
    if (--a.wake === 0) b.blink = false;
    return;
  }
  if (b.angry && update(world, b)) return;
  update(world, b);
}
